/**
 * StatusBar — 顶部 Neo HUD
 * 遥测指示灯（ONLINE）、时间戳、Gas 指示
 * 标题：XIN.XYZ 赛博 Logo + 副标 "NEURAL AGENT & SELF-CUSTODY VAULT"
 * （声音总开关已移至游戏区，不放顶部导航）
 */
import { useEffect, useState } from "react";
import { Wifi, Fuel, Clock, BookOpen, Server } from "lucide-react";
import { audioEngine } from "@/lib/audioEngine";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function formatStamp(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** 伪 Gas：基于分钟的稳定模拟值（仅 HUD 指示，非链上真实数据） */
function mockGasGwei(d: Date): number {
  const m = d.getMinutes();
  return 8 + (m % 7) * 2 + (d.getSeconds() % 3);
}

export function StatusBar() {
  const [now, setNow] = useState(() => new Date());
  const [netOk] = useState(true);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // 首次手势解锁 AudioContext（浏览器自动播放策略）
  useEffect(() => {
    const onFirstGesture = () => {
      void audioEngine.unlock();
      window.removeEventListener("pointerdown", onFirstGesture);
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    return () => window.removeEventListener("pointerdown", onFirstGesture);
  }, []);

  const gas = mockGasGwei(now);
  const gasLevel = gas < 12 ? "low" : gas < 22 ? "mid" : "high";

  return (
    <header className="sticky top-0 z-40 w-full border-b border-[#00f0ff]/20 bg-[#06080d]/85 backdrop-blur-md">
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-3 py-2 sm:px-5">
        {/* ── Logo 块 ── */}
        <div className="flex items-center gap-3">
          <div
            className="grid h-8 w-8 shrink-0 grid-cols-4 gap-0"
            aria-hidden="true"
          >
            {[
              "#ff5c8a",
              "#ff7ad9",
              "#8b5cf6",
              "#00f0ff",
              "#ff7ad9",
              "#00f0ff",
              "#38bdf8",
              "#8b5cf6",
              "#38bdf8",
              "#00f0ff",
              "#ff5c8a",
              "#ffe066",
              "#8b5cf6",
              "#ffe066",
              "#00f0ff",
              "#ff7ad9",
            ].map((c, i) => (
              <span key={i} style={{ background: c }} />
            ))}
          </div>
          <div className="leading-tight">
            <div className="neo-text-glow font-pixel text-[13px] tracking-[0.2em] text-[#e8f4ff] sm:text-[15px]">
              XIN<span className="text-[#ff7ad9]">.</span>XYZ
            </div>
            <div className="mt-0.5 font-pixel text-[6px] tracking-[0.22em] text-[#8aa3c2] sm:text-[7px]">
              NEURAL AGENT &amp; SELF-CUSTODY VAULT
            </div>
          </div>
        </div>

        {/* ── 右侧遥测簇 ── */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="neo-chip" title="Terminal network status">
            <span
              className={`neo-led ${netOk ? "neo-led-blink" : "neo-led-danger"}`}
            />
            <Wifi className="h-3 w-3 text-[#38bdf8]" strokeWidth={2.5} />
            <span className="text-[#38bdf8]">{netOk ? "ONLINE" : "OFFLINE"}</span>
          </span>

          <span className="neo-chip" title="Local timestamp">
            <Clock className="h-3 w-3 text-[#00f0ff]" strokeWidth={2.5} />
            <span className="tabular-nums text-[#c8e0ff]">
              {formatStamp(now)}
            </span>
          </span>

          <span
            className="neo-chip"
            title="ETH gas (simulated indicator — not live chain data)"
          >
            <Fuel
              className={`h-3 w-3 ${
                gasLevel === "low"
                  ? "text-[#38bdf8]"
                  : gasLevel === "mid"
                    ? "text-[#fbbf24]"
                    : "text-[#f97316]"
              }`}
              strokeWidth={2.5}
            />
            <span
              className={`neo-led ${
                gasLevel === "low"
                  ? ""
                  : gasLevel === "mid"
                    ? "neo-led-warn"
                    : "neo-led-danger"
              }`}
            />
            <span className="text-[#c8e0ff]">GAS {gas}</span>
          </span>

          {/* ── RPC MANAGER 入口（首页页角）：站内 /rpc/（新标签，不打断游戏局）── */}
          <a
            href="/rpc/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="RPC MANAGER — opens /rpc/ in a new tab"
            className="px-btn"
            onMouseEnter={() => audioEngine.play("beep")}
            onClick={() => audioEngine.play("select")}
          >
            <Server className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>RPC MANAGER</span>
          </a>

          {/* ── ABOUT xin.xyz 入口（首页页角）：文案 about xin.xyz，站内 /about.html（新标签保游戏局不丢）── */}
          <a
            href="/about.html"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ABOUT — opens /about.html in a new tab"
            className="px-btn"
            onMouseEnter={() => audioEngine.play("beep")}
            onClick={() => audioEngine.play("select")}
          >
            <BookOpen className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>about xin.xyz</span>
          </a>
        </div>
      </div>
    </header>
  );
}

export default StatusBar;
